import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Mail, Send, FileText, Loader2, Eye, ChevronDown, 
  Info, Copy, User, Briefcase, Receipt, Calendar, CheckSquare
} from 'lucide-react';

const CATEGORY_LABELS = {
  contact: 'General Contact',
  job_update: 'Job Updates',
  payment_reminder: 'Payment Reminders',
  upcoming_service: 'Upcoming Services',
  thank_you: 'Thank You',
  request_confirmation: 'Request Confirmation',
  custom: 'Custom'
};

const CATEGORY_ICONS = {
  contact: <Mail className="w-4 h-4" />,
  job_update: <Briefcase className="w-4 h-4" />,
  payment_reminder: <Receipt className="w-4 h-4" />,
  upcoming_service: <Calendar className="w-4 h-4" />,
  thank_you: <User className="w-4 h-4" />,
  request_confirmation: <CheckSquare className="w-4 h-4" />,
  custom: <FileText className="w-4 h-4" />
};

function EmailComposer({ 
  open, 
  onOpenChange, 
  apiClient, 
  recipient,
  customerId,
  jobId,
  invoiceId,
  estimateId,
  defaultCategory = 'contact',
  defaultVariables = {},
  companyInfo = null
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  // Live deliverability status: { log_id, status, provider, last_event, expires_at }.
  // Polls /api/email-logs/{id} every 3s for ~30s after a successful send,
  // then renders a colored badge so the sender knows whether the email
  // actually reached the inbox, hit spam, or bounced — without needing to
  // crack open Resend's dashboard.
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [form, setForm] = useState({
    subject: '',
    body: '',
    message: '' // For templates that use {{message}} placeholder
  });

  useEffect(() => {
    if (open) {
      fetchTemplates();
      setSelectedCategory(defaultCategory);
      // Reset live status so the previous send's badge doesn't bleed into
      // a new compose session.
      setDeliveryStatus(null);
    }
  }, [open, defaultCategory]);

  useEffect(() => {
    // Auto-select default template for category
    const categoryTemplates = templates.filter(t => t.category === selectedCategory);
    const defaultTemplate = categoryTemplates.find(t => t.is_default) || categoryTemplates[0];
    if (defaultTemplate) {
      setSelectedTemplateId(defaultTemplate.id);
      setForm({
        subject: defaultTemplate.subject,
        body: defaultTemplate.body,
        message: ''
      });
    } else {
      setSelectedTemplateId('');
      setForm({ subject: '', body: '', message: '' });
    }
  }, [selectedCategory, templates]);

  const fetchTemplates = async () => {
    try {
      const res = await apiClient.get('/email-templates');
      setTemplates(res.data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
    setLoading(false);
  };

  // Poll the email log every 3 seconds for up to ~30 seconds after sending.
  // Stops early once we hit a terminal status (delivered, bounced, complained)
  // since further polling won't change anything.
  useEffect(() => {
    if (!deliveryStatus?.polling || !deliveryStatus?.log_id) return;
    const TERMINAL = new Set(['delivered', 'bounced', 'complained']);
    let attempts = 0;
    const MAX_ATTEMPTS = 10; // 10 × 3s = 30s
    const tick = async () => {
      attempts += 1;
      try {
        const res = await apiClient.get(`/email-logs/${deliveryStatus.log_id}`);
        setDeliveryStatus((prev) => prev ? {
          ...prev,
          status: res.data.status || prev.status,
          provider: res.data.provider,
          events: res.data.events || [],
          polling: !TERMINAL.has(res.data.status) && attempts < MAX_ATTEMPTS,
        } : prev);
      } catch {
        // Silent — log row may briefly 404 if not yet committed.
      }
    };
    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
  }, [deliveryStatus?.polling, deliveryStatus?.log_id, apiClient]);

  const handleTemplateChange = (templateId) => {
    setSelectedTemplateId(templateId);
    if (templateId === 'custom') {
      setForm({ subject: '', body: '', message: '' });
    } else {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setForm({
          subject: template.subject,
          body: template.body,
          message: ''
        });
      }
    }
  };

  const replaceVariables = (text) => {
    if (!text) return '';
    
    const variables = {
      customer_name: recipient?.name || 'Customer',
      company_name: defaultVariables.company_name || 'Our Company',
      sender_name: defaultVariables.sender_name || 'Team',
      message: form.message || '',
      ...defaultVariables
    };
    
    let result = text;
    for (const [key, value] of Object.entries(variables)) {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value || '');
    }
    return result;
  };

  const handleSend = async () => {
    if (!form.subject || !form.body) {
      toast.error('Subject and message body are required');
      return;
    }
    
    if (!recipient?.email) {
      toast.error('Recipient email is required');
      return;
    }

    setSending(true);
    try {
      // Replace {{message}} in body with the message field
      let finalBody = form.body;
      if (form.message) {
        finalBody = finalBody.replace('{{message}}', form.message);
      }

      const res = await apiClient.post('/send-templated-email', {
        template_id: selectedTemplateId !== 'custom' ? selectedTemplateId : null,
        recipient_email: recipient.email,
        recipient_name: recipient.name,
        subject: form.subject,
        body: finalBody,
        customer_id: customerId,
        job_id: jobId,
        invoice_id: invoiceId,
        estimate_id: estimateId,
        variables: defaultVariables
      });

      toast.success('Email sent successfully!');
      // Kick off live deliverability polling — the dialog stays open so the
      // sender sees the status badge transition through queued → sent →
      // delivered (or bounced/spam). Auto-closes after 30s or when the
      // sender clicks Close.
      const logId = res?.data?.log_id;
      if (logId) {
        setDeliveryStatus({ log_id: logId, status: 'queued', polling: true });
      } else {
        // Older endpoint shape — just close as before.
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    }
    setSending(false);
  };

  const categoryTemplates = templates.filter(t => t.category === selectedCategory);

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Send Email
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            To: {recipient?.name} ({recipient?.email})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Category Selector */}
          <div>
            <Label className="text-zinc-300 mb-2 block">Email Type</Label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <Button
                  key={value}
                  variant={selectedCategory === value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(value)}
                  className={selectedCategory === value 
                    ? 'bg-primary text-black' 
                    : 'border-zinc-600 text-zinc-300 hover:bg-zinc-800'}
                >
                  {CATEGORY_ICONS[value]}
                  <span className="ml-1">{label}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Template Selector */}
          <div>
            <Label className="text-zinc-300">Template</Label>
            <Select value={selectedTemplateId} onValueChange={handleTemplateChange}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                <SelectValue placeholder="Select a template..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {categoryTemplates.map(t => (
                  <SelectItem key={t.id} value={t.id} className="text-white">
                    <div className="flex items-center gap-2">
                      {t.name}
                      {t.is_default && <Badge variant="outline" className="text-xs border-primary text-primary">Default</Badge>}
                      {t.is_system && <Badge variant="outline" className="text-xs border-zinc-500 text-zinc-400">System</Badge>}
                    </div>
                  </SelectItem>
                ))}
                <SelectItem value="custom" className="text-white">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Write Custom Email
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Subject */}
          <div>
            <Label className="text-zinc-300">Subject</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Email subject..."
              className="bg-zinc-700 border-zinc-600 text-white mt-1 placeholder:text-zinc-400"
              data-testid="email-composer-subject"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Preview: {replaceVariables(form.subject)}
            </p>
          </div>

          {/* Message field for templates with {{message}} */}
          {form.body.includes('{{message}}') && (
            <div>
              <Label className="text-zinc-300">Your Message</Label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Enter your personalized message..."
                className="bg-zinc-700 border-zinc-600 text-white mt-1 placeholder:text-zinc-400"
                rows={3}
              />
            </div>
          )}

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-zinc-300">Email Body</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreview(!showPreview)}
                className="text-zinc-400 hover:text-white"
              >
                <Eye className="w-4 h-4 mr-1" />
                {showPreview ? 'Edit' : 'Preview'}
              </Button>
            </div>
            
            {showPreview ? (
              <div className="rounded-lg overflow-hidden border border-zinc-600">
                {/* Company Header */}
                {companyInfo && (
                  <div className="bg-zinc-900 p-3 border-b border-zinc-700">
                    {companyInfo.logo_url ? (
                      <img 
                        src={companyInfo.logo_url} 
                        alt="Company Logo" 
                        className="max-h-10 max-w-[180px] object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <h3 className="text-yellow-400 font-bold text-base uppercase tracking-wider">
                        {companyInfo.name || 'Your Company'}
                      </h3>
                    )}
                  </div>
                )}
                
                {/* Email Content */}
                <div className="bg-zinc-800 p-4 min-h-[150px] text-white whitespace-pre-wrap">
                  {replaceVariables(form.body.replace('{{message}}', form.message))}
                  
                  {/* Email Signature */}
                  {companyInfo?.email_signature && (
                    <div className="mt-4 pt-3 border-t border-zinc-700">
                      <p className="text-zinc-400 text-sm whitespace-pre-line">{companyInfo.email_signature}</p>
                    </div>
                  )}
                </div>
                
                {/* Company Footer */}
                {companyInfo && (
                  <div className="bg-zinc-950 p-3 border-t border-zinc-700 text-xs">
                    {companyInfo.logo_url && (
                      <img 
                        src={companyInfo.logo_url} 
                        alt={companyInfo.name}
                        className="h-6 max-w-[120px] object-contain mb-1"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    {companyInfo.address && <p className="text-zinc-500">{companyInfo.address}</p>}
                    <p>
                      {companyInfo.phone && <span className="text-yellow-400">{companyInfo.phone}</span>}
                      {companyInfo.phone && companyInfo.email && <span className="text-zinc-500"> | </span>}
                      {companyInfo.email && <span className="text-yellow-400">{companyInfo.email}</span>}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <Textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Email body..."
                className="bg-zinc-700 border-zinc-600 text-white mt-1 placeholder:text-zinc-400"
                rows={8}
              />
            )}
          </div>

          {/* Variables Help */}
          <div className="bg-zinc-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
              <Info className="w-4 h-4" />
              Available Variables (click to copy)
            </div>
            <div className="flex flex-wrap gap-2">
              {['customer_name', 'company_name', 'sender_name', 'job_title', 'scheduled_date'].map(v => (
                <Badge
                  key={v}
                  variant="outline"
                  className="border-zinc-600 text-zinc-300 cursor-pointer hover:bg-zinc-700"
                  onClick={() => {
                    navigator.clipboard.writeText(`{{${v}}}`);
                    toast.success(`Copied {{${v}}}`);
                  }}
                >
                  {`{{${v}}}`}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Live deliverability badge — appears after send and updates as
            Resend's webhook fires delivered / bounced / complained events. */}
        {deliveryStatus && (
          <div
            className={`mx-1 mb-2 px-3 py-2 rounded-md border text-sm flex items-center justify-between ${
              deliveryStatus.status === 'delivered' ? 'bg-emerald-950/40 border-emerald-700 text-emerald-200' :
              deliveryStatus.status === 'bounced' ? 'bg-red-950/40 border-red-700 text-red-200' :
              deliveryStatus.status === 'complained' ? 'bg-amber-950/40 border-amber-700 text-amber-200' :
              deliveryStatus.status === 'sent_smtp' ? 'bg-zinc-800 border-zinc-600 text-zinc-300' :
              'bg-blue-950/40 border-blue-700 text-blue-200'
            }`}
            data-testid="delivery-status-banner"
          >
            <div className="flex items-center gap-2">
              {deliveryStatus.polling && <Loader2 className="w-4 h-4 animate-spin" />}
              <span data-testid="delivery-status-label">
                {deliveryStatus.status === 'queued' && 'Queued — handing off to provider…'}
                {deliveryStatus.status === 'sent' && 'Sent via Resend — waiting for delivery confirmation…'}
                {deliveryStatus.status === 'delivered' && '✓ Delivered to recipient inbox'}
                {deliveryStatus.status === 'bounced' && '⚠ Bounced — invalid address or mailbox unavailable'}
                {deliveryStatus.status === 'complained' && '⚠ Marked as spam by recipient'}
                {deliveryStatus.status === 'delayed' && 'Delivery delayed — provider will retry'}
                {deliveryStatus.status === 'sent_smtp' && 'Sent via SMTP — no delivery confirmation available'}
                {deliveryStatus.status === 'opened' && '✓ Delivered & opened'}
                {deliveryStatus.status === 'clicked' && '✓ Delivered, opened & link clicked'}
              </span>
            </div>
            {!deliveryStatus.polling && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenChange(false)}
                className="text-xs"
                data-testid="delivery-status-close"
              >
                Close
              </Button>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-600">
            {deliveryStatus ? 'Close' : 'Cancel'}
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={sending || !form.subject || !form.body || !!deliveryStatus}
            className="bg-primary text-black hover:bg-primary/90"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EmailComposer;
